import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';

const colors = {
  primaryBlue: "rgb(15, 140, 227)",
  slateGray: "rgb(70, 85, 105)",
  earthBrown: "rgb(120, 85, 60)",
  lightGray: "rgb(245, 247, 250)",
};

const servicesData = [
  {
    key: "development",
    imgSrc: "image/S1.jpg",
    color: colors.primaryBlue,
  },
  {
    key: "management",
    imgSrc: "image/S2.jpg",
    color: colors.slateGray,
  },
  {
    key: "construction",
    imgSrc: "image/S3.jpg",
    color: colors.earthBrown,
  },
];

const Services = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const direction = i18n.language === "ar" ? "rtl" : "ltr";
  const textAlign = direction === "rtl" ? "right" : "left";

  const handleClick = () => {
    navigate('/service');
  };

  return (
    <Box
      sx={{
        minHeight: "90vh",
        p: { xs: 3, sm: 6 },
        background: `url('image/back1.jpg') no-repeat center center / cover, ${colors.lightGray}`,
        fontFamily: "'Roboto', sans-serif",
        direction,
        perspective: "1500px", // ✨ 3D Perspective
      }}
    >
      <>
        <style>
          {`
            @keyframes fadeSlideUp {
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }

            @keyframes pulseGlow {
              0%, 100% {
                text-shadow: 2px 2px 6px rgba(255, 255, 255, 0.4);
              }
              50% {
                text-shadow: 2px 2px 12px rgba(255, 255, 255, 0.8);
              }
            }
          `}
        </style>

        <Typography
          variant="h3"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 800,
            color: "white",
            mb: 4,
            fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
            opacity: 0,
            transform: "translateY(30px)",
            animation:
              "fadeSlideUp 1.2s ease-out forwards, pulseGlow 3s ease-in-out infinite",
            textShadow: "2px 2px 6px rgb(255, 255, 255)",
            transition: "color 0.3s ease, text-shadow 0.3s ease",
            "&:hover": {
              color: "#0056b3",
              textShadow: "3px 3px 12px rgba(255, 255, 255, 0.9)",
            },
          }}
        >
          {t("services.title")}
        </Typography>
      </>

      <Grid container spacing={{ xs: 3, md: 5 }} justifyContent="center">
        {servicesData.map(({ key, imgSrc, color }, idx) => (
          <Grid key={idx} item xs={12} sm={6} md={4}>
            <Card
              elevation={0}
              sx={{
                height: "500px",
                borderRadius: 10,
                backgroundColor: "#fff",
                position: "relative",
                px: 3,
                py: 4,
                minHeight: { xs: 300, sm: 480 },
                maxHeight: { xs: 350, sm: 520 },
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                border: `6px solid lightgray`,
                transformStyle: "preserve-3d",
                transition: "transform 0.6s ease, box-shadow 0.6s ease",
                boxShadow: `0 10px 30px rgba(0,0,0,0.15), 0 20px 40px rgba(0,0,0,0.1)`,
                "&:hover": {
                  transform: "rotateY(3deg) rotateX(3deg) scale(1.03)",
                  boxShadow: `0 20px 60px rgba(0,0,0,0.25)`,
                },
              }}
            >
              <Box
                sx={{
                  mb: 3,
                  userSelect: "none",
                  position: "relative",
                  width: "100%",
                  paddingTop: "56.25%",
                  borderRadius: 2,
                  overflow: "hidden",
                  boxShadow: `0 4px 8px ${color}50`,
                  "& img": {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transition: "transform 0.4s ease, filter 0.4s ease",
                  },
                  "&:hover img": {
                    transform: "scale(1.08)",
                    filter: `drop-shadow(0 10px 20px ${color}80)`,
                  },
                }}
              >
                <img src={imgSrc} alt={t(`services.${key}.title`)} loading="lazy" />
              </Box>

              <CardContent sx={{ px: 0, flexGrow: 1 }}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    fontWeight: "700",
                    color: color,
                    textAlign,
                    letterSpacing: 0.8,
                  }}
                >
                  {t(`services.${key}.title`)}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    textAlign,
                    lineHeight: 1.6,
                    minHeight: 90,
                    px: 1,
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                  }}
                >
                  {t(`services.${key}.description`)}
                </Typography>
              </CardContent>

              <Box sx={{ textAlign: "center", mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleClick}
                  sx={{
                    backgroundColor: color,
                    color: "#fff",
                    fontWeight: "700",
                    px: { xs: 3, sm: 5 },
                    py: 1.5,
                    borderRadius: 3,
                    boxShadow: `0 6px 14px ${color}80`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: color,
                      boxShadow: `0 10px 24px ${color}B3`,
                      filter: "brightness(0.95)",
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  {t("services.learnMore")}
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Services;
